import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/ProfileMenu.css";
import * as Sentry from '@sentry/react';

export default function ProfileMenu({ user }) {
    const navigate = useNavigate();

    const [open, setOpen] = useState(false);
    const [logoutLoading, setLogoutLoading] = useState(false);
    const [billingLoading, setBillingLoading] = useState(false);

    const wrapperRef = useRef(null);
    const closeTimeoutRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        return () => {
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
                closeTimeoutRef.current = null;
            }
        };
    }, []);

    const handleMouseEnter = () => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        setOpen(true);
    };

    const handleMouseLeave = () => {
        closeTimeoutRef.current = setTimeout(() => {
            setOpen(false);
        }, 200);
    };

    if (!user?.id) return null;

    const hasResume = !!(user.resume_file && user.resume_file.trim().length > 1);
    const isPremium = user.plan === "premium";
    const canManageBilling = user?.plan === "premium" && !!user?.subscription_status;

    const resumeButtonText = hasResume ? "Change Resume" : "Enter Resume";

    // NOTE: Based on your last sentence ("Upgrade to enter in a new resume"),
    // this disabled state makes the most sense for NON-premium users who already have a resume.
    const resumeButtonDisabled = !isPremium && hasResume;

    const HandleBilling = async () => {
        setBillingLoading(true);

        try {
            const result = await fetch("/api/get_billing", { method: "POST" });
            const data = await result.json();

            if (!result.ok) {
                if (data.error_code === 'session_expired') {
                    window.location.href = '/';
                    return;
                }
                console.error(data.error_message || 'Error loading billing.');
                return;
            }

            if (data.redirect) {
                window.location.href = data.redirect;
                return;
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error loading billing.');
        } finally {
            setBillingLoading(false);
        }
    };

    return (
        <div
            ref={wrapperRef}
            className="profile-menu"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div
                className="profile-trigger"
                role="button"
                tabIndex={0}
                onClick={() => navigate("/profile")}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate("/profile");
                    }
                }}
                aria-label={`${user.first_name}'s Profile`}
            >
                {user.first_name}'s Profile
            </div>

            {open && (
                <div
                    className="profile-dropdown"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    <p className="profile-line">
                        <span className="profile-email-wrap">
                            <span className="profile-email-text">
                                {user.email}
                            </span>
                            {user.auth_type === "google" && (
                                <img
                                    src="/static/google-logo.png"
                                    alt="Google"
                                    className="profile-auth-icon"
                                />
                            )}
                        </span>
                    </p>

                    <p className="profile-line">
                        <span>
                            Resume:{" "}
                            {hasResume
                                ? `'${user.resume_file}'`
                                : "No resume entered"}
                        </span>

                        <span className="resume-action-wrap">
                            <span
                                className={`dropdown-mini-btn ${resumeButtonDisabled ? "is-disabled" : ""}`}
                                onClick={() => {
                                    if (resumeButtonDisabled) return;
                                    navigate("/index?noredir=1");
                                }}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        if (resumeButtonDisabled) return;
                                        navigate("/index?noredir=1");
                                    }
                                }}
                            >
                                {resumeButtonText}
                            </span>

                            {resumeButtonDisabled && (
                                <span className="dropdown-tooltip">
                                    Upgrade plan to use a new resume
                                </span>
                            )}
                        </span>

                    </p>

                    <p className="profile-line">
                        <span>
                            Subscription: {user.plan === "premium" ? "Premium Plan" : "Free Plan"}{" "}
                        </span>

                        {canManageBilling && (
                            <span
                                className="dropdown-mini-btn"
                                onClick={() => {
                                    if (billingLoading) return;
                                    HandleBilling();
                                }}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if ((e.key === "Enter" || e.key === " ") && !billingLoading) {
                                        HandleBilling();
                                    }
                                }}
                                title="Manage billing"
                            >
                                {billingLoading ? (
                                    <div className="profile-menu-spinner"></div>
                                ) : (
                                    "Manage Billing"
                                )}
                            </span>
                        )}
                    </p>

                    <p className="logout-text profile-line">
                        {logoutLoading ? (
                            <div className="profile-menu-spinner"></div>
                        ) : (
                            <span
                                onClick={async () => {
                                    setLogoutLoading(true);
                                    try {
                                        const response = await fetch("/api/logout", { method: "POST" });
                                        const data = await response.json();
                                        if (!response.ok) {
                                            console.error(data.error_message || 'Error logging out.');
                                        }
                                        window.location.href = "/";
                                    } catch (err) {
                                        Sentry.captureException(err);
                                        window.location.href = "/";
                                    } finally {
                                        setLogoutLoading(false);
                                    }
                                }}
                                style={{ cursor: "pointer" }}
                            >
                                Logout
                            </span>
                        )}
                    </p>

                    <p
                        className="profile-full-link profile-line"
                        onClick={() => navigate("/profile")}
                    >
                        Manage Account
                    </p>
                </div>
            )}
        </div>
    );
}
